<template>
  <div class="checkout-page">

    <!-- Minimal checkout header -->
    <header class="checkout-header">
      <div class="header-inner">
        <router-link to="/" class="header-logo">
          <img src="@/assets/logo.svg" alt="Plug2charge.nl" class="logo-img">
        </router-link>

        <!-- 3-step progress indicator -->
        <nav class="progress-steps" aria-label="Bestelstappen">
          <div class="progress-step step-done">
            <div class="step-dot step-dot--done">
              <i class="bi bi-check2"></i>
            </div>
            <span class="step-label">Winkelwagen</span>
          </div>
          <div class="progress-connector progress-connector--done"></div>
          <div class="progress-step step-active">
            <div class="step-dot step-dot--active">2</div>
            <span class="step-label step-label--active">Bestellen</span>
          </div>
          <div class="progress-connector"></div>
          <div class="progress-step step-future">
            <div class="step-dot step-dot--future">3</div>
            <span class="step-label">Bevestiging</span>
          </div>
        </nav>

        <a href="mailto:info@plug2charge.nl" class="header-help">
          <i class="bi bi-envelope header-help__icon"></i>
          <span class="header-help__text">Hulp nodig? Mail ons</span>
        </a>
      </div>
    </header>

    <div class="checkout-body">
      <div class="checkout-layout">

        <!-- ── Left: Form ── -->
        <div class="form-column">

          <!-- Section 01: Contactgegevens -->
          <section class="form-card">
            <div class="form-card__header">
              <span class="section-number">01</span>
              <h2 class="section-title">Contactgegevens</h2>
            </div>
            <div class="form-card__body">
              <div class="field-grid">
                <div class="field field--full">
                  <label class="field__label">E-mailadres <span class="field__required">*</span></label>
                  <div class="input-icon-wrap">
                    <i class="bi bi-envelope input-icon"></i>
                    <input
                      v-model="form.email"
                      type="email"
                      autocomplete="email"
                      placeholder="jouw@email.nl"
                      required
                      class="field__input input-icon-wrap__input"
                      :class="{ 'field__input--error': errors.email }"
                    >
                  </div>
                  <p v-if="errors.email" role="alert" class="field__error">{{ errors.email }}</p>
                  <p class="field__hint">Je ontvangt je orderbevestiging op dit adres</p>
                </div>
                <div class="field">
                  <label class="field__label">Voornaam <span class="field__required">*</span></label>
                  <input
                    v-model="form.firstName"
                    type="text"
                    autocomplete="given-name"
                    placeholder="Jan"
                    required
                    class="field__input"
                    :class="{ 'field__input--error': errors.firstName }"
                  >
                  <p v-if="errors.firstName" role="alert" class="field__error">{{ errors.firstName }}</p>
                </div>
                <div class="field">
                  <label class="field__label">Achternaam <span class="field__required">*</span></label>
                  <input
                    v-model="form.lastName"
                    type="text"
                    autocomplete="family-name"
                    placeholder="Janssen"
                    required
                    class="field__input"
                    :class="{ 'field__input--error': errors.lastName }"
                  >
                  <p v-if="errors.lastName" role="alert" class="field__error">{{ errors.lastName }}</p>
                </div>
                <div class="field">
                  <label class="field__label">Telefoon</label>
                  <div class="input-icon-wrap">
                    <i class="bi bi-telephone input-icon"></i>
                    <input
                      v-model="form.phone"
                      type="tel"
                      autocomplete="tel"
                      placeholder="06 12 34 56 78"
                      class="field__input input-icon-wrap__input"
                    >
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Section 02: Verzendadres -->
          <section class="form-card">
            <div class="form-card__header">
              <span class="section-number">02</span>
              <h2 class="section-title">Verzendadres</h2>
            </div>
            <div class="form-card__body">
              <div class="field-grid">
                <div class="field field--full">
                  <label class="field__label">Straat + huisnummer <span class="field__required">*</span></label>
                  <input
                    v-model="form.address"
                    type="text"
                    autocomplete="street-address"
                    placeholder="Voorbeeldstraat 12A"
                    required
                    class="field__input"
                    :class="{ 'field__input--error': errors.address }"
                  >
                  <p v-if="errors.address" role="alert" class="field__error">{{ errors.address }}</p>
                </div>
                <div class="field">
                  <label class="field__label">Postcode <span class="field__required">*</span></label>
                  <input
                    v-model="form.postcode"
                    type="text"
                    autocomplete="postal-code"
                    placeholder="1234 AB"
                    required
                    class="field__input"
                    :class="{ 'field__input--error': errors.postcode }"
                  >
                  <p v-if="errors.postcode" role="alert" class="field__error">{{ errors.postcode }}</p>
                </div>
                <div class="field">
                  <label class="field__label">Stad <span class="field__required">*</span></label>
                  <input
                    v-model="form.city"
                    type="text"
                    autocomplete="address-level2"
                    placeholder="Amsterdam"
                    required
                    class="field__input"
                    :class="{ 'field__input--error': errors.city }"
                  >
                  <p v-if="errors.city" role="alert" class="field__error">{{ errors.city }}</p>
                </div>
                <div class="field field--full">
                  <label class="field__label">Land</label>
                  <div class="input-icon-wrap select-wrap">
                    <i class="bi bi-globe input-icon"></i>
                    <select
                      v-model="form.country"
                      class="field__input field__select input-icon-wrap__input"
                    >
                      <option value="NL">Nederland</option>
                      <option value="BE">België</option>
                      <option value="DE">Duitsland</option>
                    </select>
                    <i class="bi bi-chevron-down select-chevron"></i>
                  </div>
                </div>

                <!-- Different shipping address -->
                <div class="field field--full">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="differentShipping" class="checkbox-input">
                    <span class="checkbox-text">Bezorgen op een ander adres</span>
                  </label>
                </div>

                <template v-if="differentShipping">
                  <div class="field field--full">
                    <label class="field__label">Afleveradres</label>
                    <input
                      v-model="form.shippingAddress"
                      type="text"
                      placeholder="Afleverstraat 12"
                      class="field__input"
                    >
                  </div>
                  <div class="field">
                    <input v-model="form.shippingPostcode" type="text" placeholder="Postcode" class="field__input">
                  </div>
                  <div class="field">
                    <input v-model="form.shippingCity" type="text" placeholder="Stad" class="field__input">
                  </div>
                </template>

                <div class="field field--full">
                  <label class="field__label">Opmerking bij bestelling</label>
                  <textarea
                    v-model="form.notes"
                    rows="3"
                    placeholder="Bijv. 'bel aan bij de buren' of 'graag contactloos bezorgen'"
                    class="field__input field__textarea"
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          <!-- Section 03: Betaalmethode -->
          <section class="form-card">
            <div class="form-card__header">
              <span class="section-number">03</span>
              <h2 class="section-title">Betaalmethode</h2>
            </div>
            <div class="form-card__body">
              <div class="payment-methods">
                <label
                  v-for="pm in paymentMethods"
                  :key="pm.id"
                  class="payment-option"
                  :class="{ 'payment-option--active': selectedPayment === pm.id }"
                >
                  <input type="radio" :value="pm.id" v-model="selectedPayment" class="sr-only">
                  <!-- Custom radio -->
                  <div class="payment-radio" :class="{ 'payment-radio--active': selectedPayment === pm.id }">
                    <div v-if="selectedPayment === pm.id" class="payment-radio__dot"></div>
                  </div>
                  <!-- Betaalicoon -->
                  <span class="payment-badge">
                    <img :src="`/img/payment/${pm.key}.svg`" :alt="pm.label" loading="lazy" class="payment-badge__img" />
                  </span>
                  <div class="payment-info">
                    <p class="payment-info__label">{{ pm.label }}</p>
                    <p class="payment-info__desc">{{ pm.desc }}</p>
                  </div>
                  <i v-if="selectedPayment === pm.id" class="bi bi-check-circle payment-check"></i>
                </label>
              </div>
            </div>
          </section>

          <!-- Terms + CTA (mobile only) -->
          <div class="mobile-cta">
            <div v-if="orderError" role="alert" class="order-error">
              <i class="bi bi-exclamation-triangle order-error__icon"></i>
              <span>{{ orderError }}</span>
            </div>
            <label class="checkbox-label checkbox-label--terms" :class="{ 'checkbox-label--error': termsError }">
              <input type="checkbox" v-model="agreedToTerms" class="checkbox-input" @change="termsError = false; orderError = ''">
              <span class="checkbox-text">
                Ik ga akkoord met de
                <router-link to="/page/algemene-voorwaarden" class="terms-link">algemene voorwaarden</router-link>
                en het
                <router-link to="/page/privacy" class="terms-link">privacybeleid</router-link>.
              </span>
            </label>
            <button
              @click="placeOrder"
              :disabled="placing"
              class="submit-btn"
            >
              <i v-if="placing" class="bi bi-arrow-repeat spin"></i>
              <i v-else class="bi bi-lock-fill"></i>
              {{ placing ? 'Bestelling wordt geplaatst...' : `Bestelling plaatsen · ${formatPrice(grandTotal)}` }}
            </button>
            <p class="trust-strip">
              <i class="bi bi-shield-check trust-strip__icon"></i> Veilig betalen
              <span class="trust-sep">·</span>
              <i class="bi bi-lock trust-strip__icon"></i> SSL-beveiligd
              <span class="trust-sep">·</span>
              <i class="bi bi-arrow-return-left trust-strip__icon"></i> 30 dagen retour
            </p>
          </div>
        </div>

        <!-- ── Right: Order summary ── -->
        <aside class="summary-column">

          <!-- Summary card -->
          <div class="summary-card">
            <div class="summary-card__top-bar"></div>

            <div class="summary-card__inner">
              <div class="summary-card__heading">
                <h3 class="summary-title">Jouw bestelling</h3>
                <router-link to="/cart" class="summary-edit">Wijzigen</router-link>
              </div>

              <!-- Items -->
              <div class="summary-items">
                <div
                  v-for="item in cartItems"
                  :key="item.key"
                  class="summary-item"
                >
                  <div class="summary-item__thumb-wrap">
                    <div class="summary-item__thumb">
                      <img
                        :src="item.product.images?.[0]?.src || '/placeholder-product.jpg'"
                        :alt="item.product.name"
                        class="summary-item__img"
                      >
                    </div>
                    <span class="summary-item__qty">{{ item.quantity }}</span>
                  </div>
                  <div class="summary-item__info">
                    <p class="summary-item__name">{{ item.product.name }}</p>
                  </div>
                  <span class="summary-item__price">
                    {{ formatPrice(parseFloat(item.product.price) * item.quantity) }}
                  </span>
                </div>
              </div>

              <!-- Totals -->
              <div class="summary-totals">
                <div class="totals-row">
                  <span class="totals-row__label">Subtotaal</span>
                  <span class="totals-row__value">{{ formatPrice(subtotal) }}</span>
                </div>
                <div v-if="discount > 0" class="totals-row">
                  <span class="totals-row__label">
                    <i class="bi bi-tag totals-row__icon"></i>Korting<template v-if="cartStore.appliedCoupon"> ({{ cartStore.appliedCoupon.code }})</template>
                  </span>
                  <span class="totals-row__value totals-row__value--free">− {{ formatPrice(discount) }}</span>
                </div>
                <div class="totals-row">
                  <span class="totals-row__label">
                    <i class="bi bi-truck totals-row__icon"></i>Verzending
                  </span>
                  <span class="totals-row__value" :class="{ 'totals-row__value--free': hasFreeShipping }">
                    {{ hasFreeShipping ? 'Gratis' : formatPrice(shippingCost) }}
                  </span>
                </div>
                <div class="totals-row totals-row--tax">
                  <span class="totals-row__label">Waarvan BTW (21%)</span>
                  <span class="totals-row__value">{{ formatPrice(tax) }}</span>
                </div>
              </div>

              <div class="summary-grand">
                <span class="summary-grand__label">Totaal</span>
                <div class="summary-grand__amount">
                  <div class="summary-grand__price">{{ formatPrice(grandTotal) }}</div>
                  <div class="summary-grand__vat">Incl. BTW</div>
                </div>
              </div>
            </div>

            <!-- CTA - desktop -->
            <div class="summary-card__cta">
              <div v-if="orderError" role="alert" class="order-error">
                <i class="bi bi-exclamation-triangle order-error__icon"></i>
                <span>{{ orderError }}</span>
              </div>

              <label class="checkbox-label checkbox-label--terms" :class="{ 'checkbox-label--error': termsError }">
                <input type="checkbox" v-model="agreedToTerms" class="checkbox-input" @change="termsError = false; orderError = ''">
                <span class="checkbox-text">
                  Ik ga akkoord met de
                  <router-link to="/page/algemene-voorwaarden" class="terms-link">voorwaarden</router-link>
                  en het
                  <router-link to="/page/privacy" class="terms-link">privacybeleid</router-link>.
                </span>
              </label>

              <button
                @click="placeOrder"
                :disabled="placing"
                class="submit-btn"
              >
                <i v-if="placing" class="bi bi-arrow-repeat spin"></i>
                <i v-else class="bi bi-lock-fill"></i>
                {{ placing ? 'Bestelling wordt geplaatst...' : `Bestelling plaatsen · ${formatPrice(grandTotal)}` }}
              </button>

              <p class="trust-strip">
                <i class="bi bi-shield-check trust-strip__icon"></i> Veilig betalen
                <span class="trust-sep">·</span>
                <i class="bi bi-lock trust-strip__icon"></i> SSL-beveiligd
                <span class="trust-sep">·</span>
                <i class="bi bi-arrow-return-left trust-strip__icon"></i> 30 dagen retour
              </p>
            </div>
          </div>

          <!-- Trust signals -->
          <div class="trust-card">
            <div v-for="trust in trustItems" :key="trust.text" class="trust-item">
              <i :class="['bi trust-item__icon', trust.icon]"></i>
              <div>
                <p class="trust-item__title">{{ trust.title }}</p>
                <p class="trust-item__sub">{{ trust.text }}</p>
              </div>
            </div>
          </div>

          <!-- Review snippet -->
          <div class="review-card">
            <div class="review-score">
              <div class="review-score__number">9.2</div>
              <div class="review-score__stars">
                <i v-for="i in 5" :key="i" class="bi bi-star-fill review-star"></i>
              </div>
            </div>
            <div class="review-body">
              <p class="review-body__title">Klanten beoordelen ons</p>
              <p class="review-body__quote">"Snelle levering en top kwaliteit. Alles precies zoals beschreven!" <em>Martijn V.</em></p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore } from '@stores/cart'
import wooApi from '@api/woocommerce'
import { useAnalytics } from '@composables/useAnalytics'

const router    = useRouter()
const cartStore = useCartStore()

// ── Form state ──────────────────────────────────────────────────────────────
const form = ref({
  email: '', firstName: '', lastName: '', phone: '',
  address: '', postcode: '', city: '', country: 'NL',
  shippingAddress: '', shippingPostcode: '', shippingCity: '',
  notes: '',
})
const errors            = ref({})
const orderError        = ref('')
const differentShipping = ref(false)
const agreedToTerms     = ref(false)
const termsError        = ref(false)
const selectedPayment   = ref('mollie_wc_gateway_ideal')
const placing           = ref(false)

// ── Cart data ────────────────────────────────────────────────────────────────
const { trackBeginCheckout, trackPurchase } = useAnalytics()

const FREE_SHIPPING  = 50
const SHIPPING_COST  = 6.95

const cartItems       = computed(() => cartStore.items)
const subtotal        = computed(() => cartStore.subtotal || 0)
const discount        = computed(() => cartStore.discountAmount || 0)
const hasFreeShipping = computed(() => subtotal.value >= FREE_SHIPPING)
const shippingCost    = computed(() => hasFreeShipping.value ? 0 : SHIPPING_COST)
// Totaal ná korting - matcht wat WooCommerce/Mollie daadwerkelijk afrekent.
const grandTotal      = computed(() => Math.max(0, subtotal.value - discount.value + shippingCost.value))
const tax             = computed(() => grandTotal.value * 0.21 / 1.21)

// ── Payment methods ──────────────────────────────────────────────────────────
// Volledige catalogus. We tonen alléén de gateways die daadwerkelijk in
// WooCommerce/Mollie aanstaan (zie loadEnabledGateways) — zo krijgt een klant
// nooit een methode te zien die bij afrekenen faalt. Activeer je een methode in
// Mollie, dan verschijnt die hier automatisch.
const ALL_PAYMENT_METHODS = [
  { id: 'mollie_wc_gateway_ideal',          wcId: 'mollie_wc_gateway_ideal',          key: 'ideal',      label: 'iDEAL',      desc: 'Betaal direct via je bank' },
  { id: 'mollie_wc_gateway_bancontact',     wcId: 'mollie_wc_gateway_bancontact',     key: 'bancontact', label: 'Bancontact', desc: 'Voor onze Belgische klanten' },
  { id: 'mollie_wc_gateway_creditcard',     wcId: 'mollie_wc_gateway_creditcard',     key: 'visa',       label: 'Creditcard', desc: 'Visa, Mastercard, Amex' },
  { id: 'mollie_wc_gateway_paypal',         wcId: 'mollie_wc_gateway_paypal',         key: 'paypal',     label: 'PayPal',     desc: 'Betaal veilig met PayPal' },
  { id: 'mollie_wc_gateway_klarnapaylater', wcId: 'mollie_wc_gateway_klarnapaylater', key: 'klarna',     label: 'Klarna',     desc: 'Nu kopen, later betalen' },
  { id: 'mollie_wc_gateway_in3',            wcId: 'mollie_wc_gateway_in3',            key: 'in3',        label: 'in3',        desc: 'Betaal in 3 termijnen, 0% rente' },
]
const enabledGatewayIds = ref(null)   // Set van enabled wcId's, of null (nog niet geladen)
const paymentMethods = computed(() => {
  const idealOnly = ALL_PAYMENT_METHODS.filter(m => m.wcId === 'mollie_wc_gateway_ideal')
  if (!enabledGatewayIds.value) return idealOnly           // vóór laden: alleen de bekend-actieve iDEAL
  const f = ALL_PAYMENT_METHODS.filter(m => enabledGatewayIds.value.has(m.wcId))
  return f.length ? f : idealOnly                          // nooit een lege lijst
})

const selectedMethod = computed(() =>
  paymentMethods.value.find(m => m.id === selectedPayment.value) || paymentMethods.value[0]
)

// Haal op welke betaal-gateways enabled zijn (alleen id + status; geen settings).
async function loadEnabledGateways() {
  try {
    // Cache-buster: betaalmethodes moeten altijd de actuele status tonen, nooit een
    // achterlopende gecachte lijst (anders mist een klant zojuist geactiveerde methodes).
    const res = await fetch(`/_wc/v3/payment_gateways?_fields=id,enabled&t=${Date.now()}`)
    const list = res.ok ? await res.json() : []
    const ids = new Set((Array.isArray(list) ? list : []).filter(g => g.enabled).map(g => g.id))
    if (ids.size) enabledGatewayIds.value = ids
    if (!paymentMethods.value.some(m => m.id === selectedPayment.value)) {
      selectedPayment.value = paymentMethods.value[0]?.id || selectedPayment.value
    }
  } catch { /* val terug op iDEAL-only */ }
}

// ── Trust items ───────────────────────────────────────────────────────────────
const trustItems = [
  { icon: 'bi-truck',            title: 'Gratis verzending', text: 'Vanaf €50' },
  { icon: 'bi-shield-check',     title: 'Veilig betalen',    text: 'SSL versleuteld' },
  { icon: 'bi-arrow-clockwise',  title: '30 dagen retour',   text: 'Zonder gedoe' },
  { icon: 'bi-headset',          title: 'Expert support',    text: 'Ma–Vr 9–17 uur' },
]

// ── Helpers ──────────────────────────────────────────────────────────────────
import { formatPrice } from '@/utils/price'

// ── Validation ───────────────────────────────────────────────────────────────
const validate = () => {
  const e = {}
  if (!form.value.email)     e.email     = 'E-mailadres is verplicht'
  else if (!/\S+@\S+\.\S+/.test(form.value.email)) e.email = 'Voer een geldig e-mailadres in'
  if (!form.value.firstName) e.firstName = 'Voornaam is verplicht'
  if (!form.value.lastName)  e.lastName  = 'Achternaam is verplicht'
  if (!form.value.address)   e.address   = 'Adres is verplicht'
  if (!form.value.postcode)  e.postcode  = 'Postcode is verplicht'
  if (!form.value.city)      e.city      = 'Stad is verplicht'
  errors.value = e
  return Object.keys(e).length === 0
}

// ── Build WooCommerce order payload ──────────────────────────────────────────
const buildOrderPayload = () => {
  const f = form.value
  const billing = {
    first_name: f.firstName,
    last_name:  f.lastName,
    email:      f.email,
    phone:      f.phone || '',
    address_1:  f.address,
    address_2:  '',
    city:       f.city,
    postcode:   f.postcode,
    country:    f.country,
  }

  const shipping = differentShipping.value ? {
    first_name: f.firstName,
    last_name:  f.lastName,
    address_1:  f.shippingAddress,
    address_2:  '',
    city:       f.shippingCity,
    postcode:   f.shippingPostcode,
    country:    f.country,
  } : { ...billing }

  const line_items = cartStore.items.map(item => {
    // Stuur de prijs expliciet mee, anders neemt WooCommerce de productregel
    // niet correct mee en blijft alleen de verzendregel over (order = €6,95).
    const unitPrice = parseFloat(
      item.product.sale_price || item.product.price || item.product.regular_price || 0
    )
    const lineTotal = (unitPrice * item.quantity).toFixed(2)
    const li = {
      product_id: item.product.id,
      quantity:   item.quantity,
      subtotal:   lineTotal,
      total:      lineTotal,
    }
    // Variatie alleen meegeven als die er daadwerkelijk is (niet variation_id: 0).
    if (item.variation?.id) li.variation_id = item.variation.id
    return li
  })

  const shipping_lines = hasFreeShipping.value
    ? [{ method_id: 'free_shipping', method_title: 'Gratis verzending', total: '0.00' }]
    : [{ method_id: 'flat_rate',     method_title: 'Standaard verzending', total: String(SHIPPING_COST.toFixed(2)) }]

  const payload = {
    payment_method:       selectedMethod.value.wcId,
    payment_method_title: selectedMethod.value.label,
    set_paid: false,
    status:   'pending',
    billing,
    shipping,
    line_items,
    shipping_lines,
    customer_note: f.notes || '',
  }

  if (cartStore.appliedCoupon?.code) {
    payload.coupon_lines = [{ code: cartStore.appliedCoupon.code }]
  }

  return payload
}

// ── Place order ──────────────────────────────────────────────────────────────
onMounted(() => {
  trackBeginCheckout(cartStore.items, cartStore.subtotal)
  loadEnabledGateways()
})

const placeOrder = async () => {
  // Formulier valideren - geef duidelijke feedback bij ontbrekende velden,
  // i.p.v. stilletjes niets doen wanneer er op de knop wordt geklikt.
  if (!validate()) {
    orderError.value = 'Niet alle verplichte velden zijn ingevuld. Controleer je contact- en adresgegevens hierboven.'
    if (typeof document !== 'undefined') {
      requestAnimationFrame(() => {
        const el = document.querySelector('.field__input--error')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.focus({ preventScroll: true })
        }
      })
    }
    return
  }

  // Akkoord met voorwaarden is verplicht - geef duidelijke feedback i.p.v. een
  // dode, uitgeschakelde knop.
  if (!agreedToTerms.value) {
    termsError.value = true
    orderError.value = 'Vink eerst "Ik ga akkoord met de voorwaarden" aan om je bestelling te plaatsen.'
    return
  }

  termsError.value = false
  orderError.value = ''
  placing.value = true

  try {
    const itemsSnapshot = cartStore.items.map(i => ({
      product:  i.product,
      quantity: i.quantity,
    }))

    const payload = buildOrderPayload()
    const order   = await wooApi.createOrder(payload)

    sessionStorage.setItem('lastOrder', JSON.stringify({
      id:        order.id,
      number:    order.number,
      total:     order.total,
      email:     order.billing?.email,
      firstName: order.billing?.first_name,
      lastName:  order.billing?.last_name,
      phone:     order.billing?.phone,
      postcode:  order.billing?.postcode,
      city:      order.billing?.city,
      country:   order.billing?.country,
      items:     itemsSnapshot,
      shipping:  shippingCost.value,
    }))

    // Stuur order_id + order_key naar onze server die process_payment() aanroept
    // via de custom WP REST endpoint. Die geeft de echte Mollie checkout-URL terug.
    // We gebruiken payment_url NOOIT direct — dat is de rauwe WC order-pay URL.
    const payRes = await fetch('/api/payment/initiate', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ order_id: order.id, order_key: order.order_key }),
    })
    const payData = await payRes.json()
    if (!payRes.ok || !payData.redirect) {
      throw new Error(payData.message || 'Betaalpagina kon niet worden geladen. Probeer het opnieuw.')
    }
    const redirectUrl = payData.redirect

    // Winkelwagen NIET hier legen: als de klant bij Mollie annuleert, moet 'ie
    // z'n winkelwagen behouden. Legen gebeurt pas op de bedankt-pagina, ná een
    // bevestigde betaling.
    window.location.href = redirectUrl

  } catch (err) {
    console.error('Order aanmaken mislukt:', err)
    orderError.value = err.message || 'Er ging iets mis. Probeer het opnieuw of neem contact op.'
  } finally {
    placing.value = false
  }
}
</script>

<style scoped>
/* ── Base ───────────────────────────────────────────────────────────────────── */
.checkout-page {
  min-height: 100dvh;
  background: #f6f7f9;
  font-family: inherit;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.checkout-header {
  background: #fff;
  border-bottom: 1.5px solid #e9eaec;
  position: sticky;
  top: 0;
  z-index: 50;
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  height: 3.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.header-logo {
  text-decoration: none;
  flex-shrink: 0;
}

.logo-img {
  height: 1.75rem;
  width: auto;
}

/* ── Progress Steps ─────────────────────────────────────────────────────────── */
.progress-steps {
  display: flex;
  align-items: center;
  gap: 0;
  font-size: 0.78rem;
}

@media (max-width: 580px) {
  .progress-steps {
    display: none;
  }
}

.progress-step {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.step-dot {
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;
}

.step-dot--done {
  background: #e5e7eb;
  color: #888;
}

.step-dot--active {
  background: #FFD500;
  color: #111;
}

.step-dot--future {
  background: #e5e7eb;
  color: #aaa;
}

.step-label {
  color: #aaa;
  font-weight: 400;
  white-space: nowrap;
}

.step-label--active {
  color: #111;
  font-weight: 600;
}

.progress-connector {
  width: 2rem;
  height: 1.5px;
  background: #e5e7eb;
  margin: 0 0.5rem;
}

.progress-connector--done {
  background: #e5e7eb;
}

.header-help {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #666;
  text-decoration: none;
  transition: color 0.15s;
  flex-shrink: 0;
}

.header-help:hover {
  color: #111;
}

.header-help__icon {
  color: #FFD500;
}

.header-help__text {
  display: none;
}

@media (min-width: 640px) {
  .header-help__text {
    display: inline;
  }
}

/* ── Body layout ────────────────────────────────────────────────────────────── */
.checkout-body {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
}

.checkout-layout {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.form-column {
  flex: 1.5;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
}

.summary-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 5.5rem;
  min-width: 0;
}

@media (max-width: 900px) {
  .checkout-layout {
    flex-direction: column;
  }

  .summary-column {
    position: static;
  }
}

/* ── Form Card ──────────────────────────────────────────────────────────────── */
.form-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 4px;
  overflow: hidden;
}

.form-card__header {
  padding: 1.125rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f1f3;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.section-number {
  font-size: 0.7rem;
  font-weight: 600;
  color: #bbb;
  letter-spacing: 0.08em;
  line-height: 1;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  margin: 0;
  line-height: 1.2;
}

.form-card__body {
  padding: 1.375rem 1.5rem 1.5rem;
}

/* ── Field grid ─────────────────────────────────────────────────────────────── */
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field--full {
  grid-column: 1 / -1;
}

.field__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
  line-height: 1;
}

.field__required {
  color: #f87171;
}

.field__input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 1rem; /* ≥16px prevents iOS auto-zoom on focus */
  color: #111;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
}

.field__input:focus {
  border-color: #FFD500;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.18);
}

.field__input--error {
  border-color: #f87171;
  background: #fff5f5;
}

.field__input--valid {
  border-color: #22c55e;
  background: #f0fdf4;
}
.field__input--valid:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
}

.field__input--error:focus {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}

.field__textarea {
  resize: none;
  line-height: 1.5;
}

.field__select {
  padding-left: 2.5rem;
  cursor: pointer;
}

.field__error {
  font-size: 0.74rem;
  color: #ef4444;
  margin: 0;
}

.field__hint {
  font-size: 0.72rem;
  color: #aaa;
  margin: 0;
}

/* ── Icon input wrap ─────────────────────────────────────────────────────────── */
.input-icon-wrap {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #bbb;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 1;
}

.input-icon-wrap__input {
  padding-left: 2.5rem;
}

.select-wrap .select-chevron {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #bbb;
  font-size: 0.7rem;
  pointer-events: none;
}

/* ── Checkbox ────────────────────────────────────────────────────────────────── */
.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  appearance: none;
  -webkit-appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 0.05rem;
  flex-shrink: 0;
  border: 2px solid #cfd3da;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.checkbox-input::before {
  content: '';
  width: 0.65rem;
  height: 0.65rem;
  transform: scale(0);
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: #111;
  clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
}

.checkbox-input:hover {
  border-color: #FFD500;
}

.checkbox-input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.35);
}

.checkbox-input:checked {
  background: #FFD500;
  border-color: #FFD500;
}

.checkbox-input:checked::before {
  transform: scale(1);
}

.checkbox-text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

/* Akkoord-blok - duidelijke, klikbare balk zodat klanten 'm niet over het hoofd zien */
.checkbox-label--terms {
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  margin-bottom: 0.875rem;
  background: #fafbfc;
  border: 1px solid #e9eaec;
  border-radius: 4px;
  transition: border-color 0.15s, background 0.15s;
}

.checkbox-label--terms:hover {
  border-color: #d7dadf;
}

.checkbox-label--terms .checkbox-text {
  font-size: 0.8rem;
}

/* Foutstaat - wanneer er op "Bestelling plaatsen" wordt geklikt zonder akkoord */
.checkbox-label--error {
  border-color: #f87171;
  background: #fff5f5;
  animation: terms-shake 0.4s;
}

.checkbox-label--error .checkbox-input {
  border-color: #f87171;
}

@keyframes terms-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
}

.terms-link {
  color: #111;
  text-underline-offset: 2px;
  transition: color 0.15s;
}

.terms-link:hover {
  color: #FFD500;
}

/* ── Payment methods ─────────────────────────────────────────────────────────── */
.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 4px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: #fff;
}

.payment-option--active {
  border-color: #FFD500;
  background: #fffbeb;
}

.payment-radio {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;
}

.payment-radio--active {
  border-color: #FFD500;
  background: #FFD500;
}

.payment-radio__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #111;
}

.payment-badge {
  width: 2.75rem;
  height: 1.9rem;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 5px;
  flex-shrink: 0;
}
.payment-badge__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.payment-info {
  flex: 1;
  min-width: 0;
}

.payment-info__label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111;
  margin: 0 0 0.1rem;
}

.payment-info__desc {
  font-size: 0.75rem;
  color: #888;
  margin: 0;
}

.payment-check {
  color: #FFD500;
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* ── Submit button ───────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  background: #FFD500;
  border: none;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
  line-height: 1;
}

.submit-btn:hover:not(:disabled) {
  background: #ffb800;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.35);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.99);
  box-shadow: 0 2px 8px rgba(255, 213, 0, 0.25);
}

.submit-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.45);
}

.submit-btn:disabled,
.submit-btn--disabled {
  background: #e5e7eb;
  color: #999;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ── Trust strip ─────────────────────────────────────────────────────────────── */
.trust-strip {
  margin: 0.625rem 0 0;
  text-align: center;
  font-size: 0.72rem;
  color: #aaa;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.trust-strip__icon {
  color: #22c55e;
}

.trust-sep {
  color: #ccc;
}

/* ── Order error ─────────────────────────────────────────────────────────────── */
.order-error {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 0.83rem;
  color: #b91c1c;
  margin-bottom: 1rem;
}

.order-error__icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* ── Mobile CTA ──────────────────────────────────────────────────────────────── */
.mobile-cta {
  display: none;
}

@media (max-width: 900px) {
  .mobile-cta {
    display: block;
  }

  .mobile-cta .checkbox-label {
    margin-bottom: 1rem;
  }
}

/* ── Summary Card ────────────────────────────────────────────────────────────── */
.summary-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.summary-card__top-bar {
  /* top border handled by border-top on summary-card */
  display: none;
}

.summary-card__inner {
  padding: 1.375rem 1.5rem 0;
}

.summary-card__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.summary-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  margin: 0;
}

.summary-edit {
  font-size: 0.78rem;
  color: #FFD500;
  text-decoration: none;
  transition: color 0.15s;
}

.summary-edit:hover {
  color: #b89b00;
}

/* ── Summary items ───────────────────────────────────────────────────────────── */
.summary-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.summary-item__thumb-wrap {
  position: relative;
  flex-shrink: 0;
}

.summary-item__thumb {
  width: 3rem;
  height: 3rem;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #e9eaec;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-item__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.25rem;
}

.summary-item__qty {
  position: absolute;
  top: -0.375rem;
  right: -0.375rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #111;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-item__info {
  flex: 1;
  min-width: 0;
}

.summary-item__name {
  font-size: 0.83rem;
  font-weight: 600;
  color: #111;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.summary-item__price {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  flex-shrink: 0;
}

/* ── Totals ──────────────────────────────────────────────────────────────────── */
.summary-totals {
  border-top: 1px solid #f0f0f0;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.totals-row__label {
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.totals-row__icon {
  font-size: 0.8rem;
}

.totals-row__value {
  font-weight: 500;
  color: #111;
}

.totals-row__value--free {
  color: #16a34a;
  font-weight: 600;
}

.totals-row--tax .totals-row__label,
.totals-row--tax .totals-row__value {
  font-size: 0.8rem;
  color: #aaa;
}

.summary-grand {
  border-top: 1px solid #f0f0f0;
  padding: 1rem 0 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.summary-grand__label {
  font-size: 1rem;
  font-weight: 700;
  color: #111;
}

.summary-grand__amount {
  text-align: right;
}

.summary-grand__price {
  font-size: 1.625rem;
  font-weight: 800;
  color: #111;
  line-height: 1;
}

.summary-grand__vat {
  font-size: 0.7rem;
  color: #aaa;
  margin-top: 0.2rem;
}

/* ── Summary CTA (desktop) ───────────────────────────────────────────────────── */
.summary-card__cta {
  padding: 0 1.5rem 1.5rem;
}

@media (max-width: 900px) {
  .summary-card__cta {
    display: none;
  }
}

/* ── Trust card ──────────────────────────────────────────────────────────────── */
.trust-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 4px;
  padding: 1.125rem 1.25rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.trust-item__icon {
  font-size: 1.1rem;
  color: #FFD500;
  flex-shrink: 0;
}

.trust-item__title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.125rem;
  line-height: 1;
}

.trust-item__sub {
  font-size: 0.7rem;
  color: #aaa;
  margin: 0;
  line-height: 1;
}

/* ── Review card ─────────────────────────────────────────────────────────────── */
.review-card {
  background: #fffbeb;
  border: 1px solid rgba(255, 213, 0, 0.3);
  border-radius: 4px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.review-score {
  flex-shrink: 0;
  text-align: center;
}

.review-score__number {
  font-size: 1.75rem;
  font-weight: 800;
  color: #111;
  line-height: 1;
}

.review-score__stars {
  display: flex;
  gap: 0.125rem;
  margin-top: 0.25rem;
  justify-content: center;
}

.review-star {
  font-size: 0.65rem;
  color: #FFD500;
}

.review-body__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #111;
  margin: 0 0 0.25rem;
}

.review-body__quote {
  font-size: 0.75rem;
  color: #666;
  margin: 0;
  line-height: 1.45;
}

/* ── Utilities ───────────────────────────────────────────────────────────────── */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 0.8s linear infinite;
}

/* ── Number input ────────────────────────────────────────────────────────────── */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
input[type=number] {
  -moz-appearance: textfield;
}

/* ── Responsive fieldgrid ────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .field-grid {
    grid-template-columns: 1fr;
  }

  .field--full {
    grid-column: auto;
  }
}
</style>
